// UnitRow — a single row in the company tree. Level drives icon + tile tone +
// subtitle label; `tag`/`status`/member count render as right-aligned columns.

import { useTranslation } from 'react-i18next';
import { Icon, Chip } from '@/components/common';
import { type OrgUnit } from '@/mocks/structure';
import { useLevelConfig } from '@/api/endpoints/levelsApi';

interface UnitRowProps {
  unit: OrgUnit;
  depth: number;
  expanded: boolean;
  selected: boolean;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
}

const UnitRow = ({ unit, depth, expanded, selected, onToggle, onSelect }: UnitRowProps) => {
  const { t } = useTranslation();
  const levelConfig = useLevelConfig();
  const level = levelConfig(unit.level);
  const hasChildren = !!unit.children?.length;
  const isRoot = unit.level === 0;
  const isInactive = unit.status === 'inactive';

  const rowClass = [
    'structure-tree__row',
    isRoot && 'structure-tree__row--root',
    selected && 'is-selected',
    isInactive && 'is-inactive',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={rowClass}
      style={{ ['--depth' as string]: depth }}
      onClick={() => onSelect(unit.id)}
      role='button'
      tabIndex={0}
      aria-selected={selected}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(unit.id);
        }
      }}
    >
      <div className='structure-tree__lead'>
        {hasChildren && !isRoot ? (
          <button
            type='button'
            className='structure-tree__chevron'
            aria-label={expanded ? 'Collapse' : 'Expand'}
            aria-expanded={expanded}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(unit.id);
            }}
          >
            <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={18} />
          </button>
        ) : !isRoot ? (
          <span className='structure-tree__chevron structure-tree__chevron--placeholder' />
        ) : (
          <></>
        )}

        <span className={`structure-tree__tile structure-tree__tile--${level.tone}`}>
          <Icon name={level.icon} size={20} />
        </span>

        <span className='structure-tree__label'>
          <span className='structure-tree__name'>{unit.name}</span>
          <span className='structure-tree__sub'>
            {level.name}
            <span className='structure-tree__sep'>·</span>
            <Icon name='map-pin' size={13} />
            {unit.location}
          </span>
        </span>
      </div>

      <div className='structure-tree__meta'>
        {isInactive && (
          <Chip variant='danger' iconStart='ban'>
            {t('structure_status_inactive')}
          </Chip>
        )}
        {!isRoot && unit.tag && (
          <Chip variant='brand' iconStart='tag'>
            {unit.tag}
          </Chip>
        )}
        <span
          className='structure-tree__count'
          title={t('structure_members', { count: unit.memberCount })}
        >
          <Icon name='users' size={14} />
          {unit.memberCount}
        </span>
        {isRoot ? (
          <span className='structure-tree__roletext'>{level.name}</span>
        ) : (
          <Chip variant='neutral'>{t('structure_level', { n: unit.level })}</Chip>
        )}
        <Icon name='chevron-right' size={18} className='structure-tree__go' />
      </div>
    </div>
  );
};

export default UnitRow;
